import { router, ROUTES } from '@/router.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { SubscriptionTier } from '@/types/SubscriptionTier.ts';
import { v4 as uuidv4 } from 'uuid';
import { useUserInfoStore } from '@/stores/UserInfoStore.ts';
import { useSourceStore } from '@/stores/SourceStore.ts';
import { eventEmitter } from '@/lib/eventEmitter.ts';
import { getConfigData } from '@/utils/ConfigUtils.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { WebSocketService } from '@/ws/WebSocketService.ts';
import { attachScreenshotAction } from '@/actions/run/AttachScreenshotAction.ts';
import { captureScreenshotAction } from '@/actions/run/CaptureScreenshotAction.ts';
import { showScreenshotPreviewAction } from '@/actions/run/ShowScreenshotPreviewAction.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { fetchAiModelsAction } from '@/actions/app/FetchAiModelsAction.ts';

export const startSessionAction = async (showTrialWarning: boolean) => {
    if (await attachScreenshotAction()) return;

    const sessionStore = useSessionStore();
    if (sessionStore.aiModelCatalogStatus !== 'ready') {
        await fetchAiModelsAction();
    }

    await router.push({ name: ROUTES.index });

    const settingsStore = useSettingsStore();

    // Forcefully end onboarding
    settingsStore.isOnboarding = false;

    const profileId = settingsStore.selectedProfileId;
    if (!profileId) {
        createTextToast(
            'No profile selected',
            'Please select a valid profile from the dropdown menu.',
            'destructive',
            true,
        );

        return;
    }

    if (!hasCredits()) {
        createTextToast(
            'Insufficient trial credits',
            'Please upgrade to continue using Leetcode Wizard.',
            'destructive',
            true,
        );

        return;
    }

    const sessionId = uuidv4();

    const { profileConfig, stepConfig } = getConfigData(profileId, undefined);

    if (!profileConfig || !stepConfig) {
        createTextToast(
            'Failed to start session',
            'Something is wrong with the selected profile configuration, please reset the configuration.',
            'destructive',
            true,
        );
        return;
    }

    const profileHasPromptsWithScreenCapture =
        Object.values(PromptConfigService.getAllPromptConfigs()).filter(
            (prompt) => profileConfig.promptIds.includes(prompt.id) && prompt.includeScreenCapture,
        ).length > 0;

    const hostStore = useSourceStore();

    let screenCaptureData: string | undefined = undefined;

    if (profileHasPromptsWithScreenCapture) {
        screenCaptureData = await captureScreenshotAction(sessionId);
        if (!screenCaptureData) return;
    }

    if (screenCaptureData && showTrialWarning && shouldShowTrialInputWarning()) {
        eventEmitter.emit('showTrialInputWarning', {
            inputSourceName: hostStore.selectedSourceName,
            screenCaptureData: screenCaptureData,
        });

        return;
    }

    const clientStore = useClientStore();
    const screenCaptures = screenCaptureData ? [screenCaptureData] : [];
    const captureRevision = screenCaptures.length;
    clientStore.$patch({
        activeSessionId: sessionId,
        activeProfileId: profileConfig.id,
        screenCapturesBySession: { [sessionId]: screenCaptures },
        screenCaptureRevisions: { [sessionId]: captureRevision },
    });

    // @TODO: First runPrompt API call may fail due to auth etc. Move this?
    showScreenshotPreviewAction(screenCaptureData, screenCaptures.length);

    WebSocketService.instance.verifyConnection();

    await router.push({
        name: ROUTES.runIndex,
        params: {
            sessionId: sessionId,
            profileConfigId: profileConfig.id,
            stepConfigId: stepConfig.id,
            variableChainHash: '_',
        },
        query: { captureRevision: String(captureRevision) },
    });
};

const hasCredits = (): boolean => {
    const userInfoStore = useUserInfoStore();

    if (userInfoStore.subscriptionTier === SubscriptionTier.Trial) {
        if (userInfoStore.creditsRemaining <= 0) {
            return false;
        }
    }

    return true;
};

const shouldShowTrialInputWarning = () => {
    const userInfoStore = useUserInfoStore();
    const hostStore = useSourceStore();

    return (
        userInfoStore.subscriptionTier === SubscriptionTier.Trial &&
        (!hostStore.selectedSourceName ||
            !hostStore.selectedSourceName.toLowerCase().includes('leetcode'))
    );
};
